import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import {
  joinSpeakerTurns,
  speakerOrdinals,
} from '@src/services/transcript/speakerTurns';

import type { ReelSection } from './deriveReelSections';
import type { ReelChapter, ReelSectionName } from './reelTypes';
import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type { Timeline } from '@src/editor/models/Timeline';

/**
 * PLAT-11099 — the whole reel, in the shape the chapterization prompt was told
 * to expect (PLAT-11152).
 *
 * ```text
 * Section 1 — Cold open
 * Speaker 1: …
 *
 * Section 2
 * Speaker 1: …
 * ```
 *
 * **Why this is composed here and not by the backend.** The epic's E4 says
 * context travels by reference and PLAT-11100 composes this server-side from
 * Section markers. That ticket is blocked on two problems in how a reel is
 * stored — the persisted video source carries no `reelSectionId` to join on, and
 * `video_json` is nulled and offloaded to S3 for a recently-saved clip — so the
 * epic's own chapterization stories would have waited on a backend investigation
 * to finish. Composing client-side is what the two naming prompts already do
 * (PLAT-11059, PLAT-11064) and what Smart Graphics does, so it is a path the
 * codebase already trusts rather than a new one.
 *
 * The deviation is scoped to *where the words are composed*: the request is
 * otherwise the one PLAT-11099 designed. When PLAT-11100 lands, the change is
 * this module and the repository's body — the run, the validation and the
 * overlay do not know which half composed the text.
 *
 * Pure — no React, no MobX, no store access.
 */

/** The DatoCMS `ai_prompt` record this text is written for (PLAT-11152). */
export const CHAPTERIZE_PROMPT_TITLE = 'Reel Util | Chapterize a Reel';

/**
 * Budget per Section. Lower than the naming prompts' because a whole reel of
 * them goes in one request: at twenty Sections this is still a bounded ask, and
 * the prompt is told that long Sections arrive abridged.
 */
export const CHAPTERIZE_SECTION_MAX_CHARS = 500;

/** Sentence end followed by whitespace — the fallback cut when a turn is long. */
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+/;

export type ChapterizationBaseText = {
  /** The composed reel, or `''` when no Section had anything to read. */
  text: string;
  /**
   * The Sections the text actually describes, in the order it describes them —
   * `Section N` in the text is `sectionIds[N - 1]`.
   *
   * This is the ordinal map the response is read against, and it is returned
   * rather than assumed because a Section with nothing left to read is **left
   * out**: a heading with no words under it says only that something is missing,
   * and numbering around the gap would silently shift every ordinal after it.
   * A Section that is not in the conversation keeps its place in the reel and
   * falls into whichever chapter it lands under, which needs no bookkeeping —
   * membership is derived from position (DE12). Sections that are turned off are
   * absent for the same reason and by the same mechanism (PLAT-11153 S7): they
   * are not on the timeline at all.
   */
  sectionIds: string[];
};

/**
 * @param sections the reel as it plays — live Sections only, in playback order
 * @param names the reel's stored Section names, for the headings
 * @param chapters the reel's chapters, so a run that must fit around the ones it
 *   is not allowed to touch can see them (PLAT-11154 S4). A model asked to group
 *   four Sections in the middle of a reel, with no idea what the chapters on
 *   either side are called, will happily name one of them the same thing.
 */
export function chapterizationBaseText(
  timeline: Timeline,
  sections: ReadonlyArray<Pick<ReelSection, 'id' | 'elementIds'>>,
  names?: Record<string, ReelSectionName | undefined>,
  chapters?: ReadonlyArray<Pick<ReelChapter, 'firstSectionId' | 'title'>>,
): ChapterizationBaseText {
  // Only chapters that have a name and open at a Section being sent: a bare
  // `Chapter:` line says nothing, and one over a Section the model cannot see
  // would point at the wrong place.
  const chapterTitleAt = new Map<string, string>();
  for (const chapter of chapters ?? []) {
    const title = chapter.title?.trim();
    if (title && !chapterTitleAt.has(chapter.firstSectionId)) {
      chapterTitleAt.set(chapter.firstSectionId, title);
    }
  }

  // Read once, then numbered across the whole reel rather than per Section: a
  // run asked to alternate the voices has to be able to tell that Section 9 is
  // the same person as Section 1, and per-passage ordinals cannot say that. On a
  // reel with one voice there is nothing to distinguish, so the map is left off
  // and the passages read without labels exactly as they did.
  // Indexed once for the whole reel, not once per Section. A reel's transcript
  // runs to thousands of elements and this is called with twenty Sections, so
  // rebuilding the map inside the loop is the O(N×M) walk CLAUDE.md's pitfall 6
  // is about.
  const elementsById = transcriptElementsById(timeline);
  const read = sections.map(section => ({
    section,
    tokens: sectionTokens(elementsById, section.elementIds),
  }));
  const ordinals = speakerOrdinals(read.flatMap(entry => entry.tokens));
  const reelOrdinals = ordinals.size > 1 ? ordinals : undefined;

  const blocks: string[] = [];
  const sectionIds: string[] = [];

  for (const { section, tokens } of read) {
    const said = capPassage(
      joinSpeakerTurns(tokens, reelOrdinals),
      CHAPTERIZE_SECTION_MAX_CHARS,
    );
    if (!said) continue;

    sectionIds.push(section.id);
    // Only a *stored* name reaches the heading. `openingWords` is a display
    // fallback for an unnamed Section, and six words of speech under a name
    // heading would teach the model that a sentence fragment is what a title
    // looks like here (PLAT-11058 DS9).
    const storedName = names?.[section.id]?.name?.trim();
    const heading = storedName
      ? `Section ${sectionIds.length} — ${storedName}`
      : `Section ${sectionIds.length}`;
    // The chapter's name goes above the Section that opens it, which is the
    // shape the prompt describes.
    const chapterTitle = chapterTitleAt.get(section.id);
    blocks.push(
      chapterTitle
        ? `Chapter: ${chapterTitle}\n${heading}\n${said}`
        : `${heading}\n${said}`,
    );
  }

  return { text: blocks.join('\n\n'), sectionIds };
}

const isActive = (token: { state?: string }) =>
  token.state !== 'removed' && token.state !== 'cut';

/** The reel's transcript elements, indexed by id — built once per composition. */
function transcriptElementsById(
  timeline: Timeline,
): Map<string, VideoTranscriptElement> {
  const track = timeline.tracks.find(
    (t): t is TranscriptTrack => t instanceof TranscriptTrack,
  );
  return new Map(track?.elements.map(el => [el.id, el]) ?? []);
}

/**
 * One Section's surviving tokens, punctuation included, in reading order.
 *
 * `getWordsWithPunctuation` rather than `getWords`: sentence boundaries have to
 * survive, both for the model and for {@link capPassage}. Removed and cut words
 * are dropped — they are grey strikethroughs in the transcript and absent from
 * the render, so the model must not read them.
 */
function sectionTokens(
  elementsById: ReadonlyMap<string, VideoTranscriptElement>,
  elementIds: readonly string[],
) {
  return elementIds.flatMap(id => {
    const element = elementsById.get(id);
    if (!element) return [];
    return element
      .getClipTranscriptSegment()
      .getWordsWithPunctuation()
      .filter(isActive);
  });
}

/**
 * Bound a passage by keeping its opening and closing and dropping the middle —
 * the shape the prompt is told to expect for a long Section.
 *
 * Cuts at the coarsest boundary that works: whole speaker turns, then whole
 * sentences within one turn, then whole words. Deterministic, so the same reel
 * always composes to the same string and a prompt tuned on it stays tuned.
 *
 * A near-twin of the naming module's own cap. Not shared with it: that module
 * ships in PLAT-11059/11064 and is in review, and reaching into it to export its
 * internals would reshape reviewed code to save a dozen lines here. If a third
 * caller ever wants this, that is the point to extract it.
 */
function capPassage(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;

  const lines = text.split('\n');
  if (lines.length > 1) return keepEnds(lines, maxChars, '\n');

  const sentences = text.split(SENTENCE_BOUNDARY);
  if (sentences.length > 1) return keepEnds(sentences, maxChars, ' ');

  return keepEnds(text.split(' '), maxChars, ' ');
}

/**
 * Take parts from the front until half the budget is spent, then from the back
 * until the whole of it is, preserving order. `spent` counts separators exactly
 * as they are added, so the result is within `maxChars` rather than about it.
 *
 * The one overrun: a single part longer than the budget is still kept, because a
 * Section that composes to nothing would drop out of the conversation entirely.
 */
function keepEnds(
  parts: readonly string[],
  maxChars: number,
  separator: string,
): string {
  const half = Math.floor(maxChars / 2);

  const opening: string[] = [];
  let spent = 0;
  for (const part of parts) {
    const cost = part.length + (opening.length ? separator.length : 0);
    if (opening.length && spent + cost > half) break;
    opening.push(part);
    spent += cost;
  }

  const closing: string[] = [];
  for (let i = parts.length - 1; i >= opening.length; i -= 1) {
    const cost = parts[i].length + separator.length;
    if (spent + cost > maxChars) break;
    closing.unshift(parts[i]);
    spent += cost;
  }

  return [...opening, ...closing].join(separator);
}
